import React, { useEffect, useMemo } from "react"
import { Tab, Tabs } from "react-bootstrap"
import { useTranslation } from "react-i18next"

import { useEvent } from "shared/lib/useEvent"
import { Chip } from "shared/ui/chip/Chip"
import { Icon } from "shared/ui/icon/Icon"
import { IconName } from "shared/ui/icon/__generated__/IconName"

import { GlobalSearchIconMap } from "features/global-search/lib/globalSearchIconMap"
import {
  GLOBAL_SEARCH_ALL_TAB,
  GlobalSearchActiveTab,
  GlobalSearchResultType
} from "features/global-search/types/GlobalSearch"

import { useGlobalSearchContext } from "../../GlobalSearchContext"
import { GlobalSearchAllTab } from "./GlobalSearchAllTab"
import { GlobalSearchBuildingReferenceTab } from "./GlobalSearchBuildingReferenceTab"
import { GlobalSearchProductTab } from "./GlobalSearchProductTab"
import { GlobalSearchProjectTab } from "./GlobalSearchProjectTab"

function GlobalSearchTabTitle({
  title,
  icon,
  count
}: {
  title: string
  icon?: IconName
  count: number
}) {
  return (
    <div className="global-search__tab-title">
      {icon ? <Icon name={icon} /> : null}
      <div className="global-search__tab-title-text">{title}</div>
      <Chip className="chip--count">{count}</Chip>
    </div>
  )
}

export const GlobalSearchTabs = React.memo(() => {
  const { t } = useTranslation()
  const { options, activeTab, hasResults, onActiveTabChange } =
    useGlobalSearchContext()

  const visibleTypeTabs = useMemo(
    () =>
      (
        [
          GlobalSearchResultType.PRODUCT,
          GlobalSearchResultType.PROJECT,
          GlobalSearchResultType.BUILDING_REFERENCE
        ] as const
      ).filter(type => (options[type]?.options?.length ?? 0) > 0),
    [options]
  )

  const totalCount = useMemo(
    () =>
      Object.values(options).reduce(
        (sum, group) => sum + (group?.options.length ?? 0),
        0
      ),
    [options]
  )

  useEffect(() => {
    if (
      activeTab !== GLOBAL_SEARCH_ALL_TAB &&
      !(visibleTypeTabs as readonly string[]).includes(activeTab)
    ) {
      onActiveTabChange(GLOBAL_SEARCH_ALL_TAB)
    }
  }, [activeTab, visibleTypeTabs, onActiveTabChange])

  const handleSelect = useEvent((key: string | null) => {
    if (key !== null) onActiveTabChange(key as GlobalSearchActiveTab)
  })

  if (!hasResults) {
    return null
  }

  return (
    <div className="global-search__tabs">
      <Tabs activeKey={activeTab} onSelect={handleSelect}>
        <Tab
          eventKey={GLOBAL_SEARCH_ALL_TAB}
          title={
            <GlobalSearchTabTitle
              title={t("GlobalSearch.All")}
              count={totalCount}
            />
          }
        >
          <GlobalSearchAllTab />
        </Tab>
        {visibleTypeTabs.includes(GlobalSearchResultType.PRODUCT) && (
          <Tab
            eventKey={GlobalSearchResultType.PRODUCT}
            title={
              <GlobalSearchTabTitle
                title={t("Product.Products")}
                count={options.PRODUCT?.options.length ?? 0}
                icon={GlobalSearchIconMap[GlobalSearchResultType.PRODUCT]}
              />
            }
          >
            <GlobalSearchProductTab />
          </Tab>
        )}
        {visibleTypeTabs.includes(GlobalSearchResultType.PROJECT) && (
          <Tab
            eventKey={GlobalSearchResultType.PROJECT}
            title={
              <GlobalSearchTabTitle
                title={t("Project.Projects")}
                count={options.PROJECT?.options.length ?? 0}
                icon={GlobalSearchIconMap[GlobalSearchResultType.PROJECT]}
              />
            }
          >
            <GlobalSearchProjectTab />
          </Tab>
        )}
        {visibleTypeTabs.includes(
          GlobalSearchResultType.BUILDING_REFERENCE
        ) && (
          <Tab
            eventKey={GlobalSearchResultType.BUILDING_REFERENCE}
            title={
              <GlobalSearchTabTitle
                title={t("BuildingReference.References")}
                count={options.BUILDING_REFERENCE?.options.length ?? 0}
                icon={
                  GlobalSearchIconMap[GlobalSearchResultType.BUILDING_REFERENCE]
                }
              />
            }
          >
            <GlobalSearchBuildingReferenceTab />
          </Tab>
        )}
      </Tabs>
    </div>
  )
})
